// Chemin: C:\gestion-copro\src\pages\admin\InterlocuteurFormPage.tsx
// Session 58 : Correction bug coproprietaireId → coproprieteIds[0]

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';

// Types
interface Contact {
  nom: string;
  prenom: string;
  fonction: string;
  email: string;
  telephone: string;
  principal: boolean;
}

interface Adresse {
  rue: string;
  codePostal: string;
  ville: string;
}

type InterlocuteurType = 'syndic' | 'promoteur' | 'constructeur' | 'prestataire' | 'assurance' | 'avocat' | 'expert' | 'autre';

// Labels pour les types
const TYPE_OPTIONS: { value: InterlocuteurType; label: string }[] = [
  { value: 'syndic', label: 'Syndic' },
  { value: 'promoteur', label: 'Promoteur' },
  { value: 'constructeur', label: 'Constructeur' },
  { value: 'prestataire', label: 'Prestataire' },
  { value: 'assurance', label: 'Assurance' },
  { value: 'avocat', label: 'Avocat' },
  { value: 'expert', label: 'Expert' },
  { value: 'autre', label: 'Autre' },
];

// Contact vide par défaut
const EMPTY_CONTACT: Contact = {
  nom: '',
  prenom: '',
  fonction: '',
  email: '',
  telephone: '',
  principal: true,
};

export default function InterlocuteurFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id && id !== 'nouveau';
  
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  
  const coproprieteId = userProfile?.coproprieteIds?.[0];

  // État du formulaire
  const [type, setType] = useState<InterlocuteurType>('syndic');
  const [raisonSociale, setRaisonSociale] = useState('');
  const [siret, setSiret] = useState('');
  const [adresse, setAdresse] = useState<Adresse>({ rue: '', codePostal: '', ville: '' });
  const [contacts, setContacts] = useState<Contact[]>([{ ...EMPTY_CONTACT }]);
  const [notes, setNotes] = useState('');
  const [actif, setActif] = useState(true);

  // États UI
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Charger les données en mode édition
  useEffect(() => {
    const fetchInterlocuteur = async () => {
      if (!isEditMode || !id) return;

      try {
        const docRef = doc(db, 'interlocuteurs', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setType(data.type || 'autre');
          setRaisonSociale(data.raisonSociale || '');
          setSiret(data.siret || '');
          setAdresse(data.adresse || { rue: '', codePostal: '', ville: '' });
          setContacts(data.contacts?.length > 0 ? data.contacts : [{ ...EMPTY_CONTACT }]);
          setNotes(data.notes || '');
          setActif(data.actif !== false);
        } else {
          setError('Interlocuteur non trouvé');
        }
      } catch (err) {
        console.error('Erreur chargement:', err);
        setError('Erreur lors du chargement');
      } finally {
        setLoadingData(false);
      }
    };

    fetchInterlocuteur();
  }, [id, isEditMode]);

  // Gérer les contacts
  const updateContact = (index: number, field: keyof Contact, value: string | boolean) => {
    const newContacts = [...contacts];
    newContacts[index] = { ...newContacts[index], [field]: value };
    
    // Si on définit ce contact comme principal, retirer le principal des autres
    if (field === 'principal' && value === true) {
      newContacts.forEach((c, i) => {
        if (i !== index) c.principal = false;
      });
    }
    
    setContacts(newContacts);
  };

  const addContact = () => {
    setContacts([...contacts, { ...EMPTY_CONTACT, principal: false }]);
  };

  const removeContact = (index: number) => {
    if (contacts.length <= 1) return;
    
    const newContacts = contacts.filter((_, i) => i !== index);
    
    // S'assurer qu'il y a toujours un contact principal
    if (!newContacts.some(c => c.principal)) {
      newContacts[0].principal = true;
    }
    
    setContacts(newContacts);
  };

  // Soumettre le formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!coproprieteId) {
      setError('Aucune copropriété associée');
      return;
    }

    if (!raisonSociale.trim()) {
      setError('La raison sociale est obligatoire');
      return;
    }

    // Vérifier qu'au moins un contact a un nom
    const validContacts = contacts.filter(c => c.nom.trim());
    if (validContacts.length === 0) {
      setError('Au moins un contact avec un nom est requis');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const interlocuteurData = {
        coproprieteId,
        type,
        raisonSociale: raisonSociale.trim(),
        siret: siret.trim() || null,
        adresse: adresse.rue.trim() ? adresse : null,
        contacts: validContacts,
        notes: notes.trim() || null,
        actif,
        updatedAt: Timestamp.now(),
      };

      if (isEditMode && id) {
        // Mise à jour
        await updateDoc(doc(db, 'interlocuteurs', id), interlocuteurData);
      } else {
        // Création
        const newDocRef = doc(db, 'interlocuteurs', `${coproprieteId}_${Date.now()}`);
        await setDoc(newDocRef, {
          ...interlocuteurData,
          createdAt: Timestamp.now(),
        });
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/admin/interlocuteurs');
      }, 1500);
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      setError('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  // Chargement initial
  if (loadingData) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto">
        {/* En-tête */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/interlocuteurs')}
            className="text-gray-600 hover:text-gray-900 mb-4 inline-flex items-center"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour à la liste
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Modifier l\'interlocuteur' : 'Nouvel interlocuteur'}
          </h1>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            {isEditMode ? 'Interlocuteur mis à jour avec succès !' : 'Interlocuteur créé avec succès !'}
          </div>
        )}

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informations générales */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations générales</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type *
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as InterlocuteurType)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Raison sociale */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Raison sociale *
                </label>
                <input
                  type="text"
                  value={raisonSociale}
                  onChange={(e) => setRaisonSociale(e.target.value)}
                  placeholder="Ex: Square Habitat Nord de France"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* SIRET */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SIRET
                </label>
                <input
                  type="text"
                  value={siret}
                  onChange={(e) => setSiret(e.target.value)}
                  placeholder="123 456 789 00012"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Statut */}
              <div className="flex items-center">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={actif}
                    onChange={(e) => setActif(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Interlocuteur actif</span>
                </label>
              </div>
            </div>
          </div>

          {/* Adresse */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Adresse</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rue
                </label>
                <input
                  type="text"
                  value={adresse.rue}
                  onChange={(e) => setAdresse({ ...adresse, rue: e.target.value })}
                  placeholder="123 rue de la Paix"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code postal
                  </label>
                  <input
                    type="text"
                    value={adresse.codePostal}
                    onChange={(e) => setAdresse({ ...adresse, codePostal: e.target.value })}
                    placeholder="62000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ville
                  </label>
                  <input
                    type="text"
                    value={adresse.ville}
                    onChange={(e) => setAdresse({ ...adresse, ville: e.target.value })}
                    placeholder="Arras"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Contacts */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Contacts</h2>
              <button
                type="button"
                onClick={addContact}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                + Ajouter un contact
              </button>
            </div>

            <div className="space-y-6">
              {contacts.map((contact, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-700">Contact {index + 1}</span>
                      {contact.principal && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                          Principal
                        </span>
                      )}
                    </div>
                    {contacts.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeContact(index)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Supprimer
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom *
                      </label>
                      <input
                        type="text"
                        value={contact.nom}
                        onChange={(e) => updateContact(index, 'nom', e.target.value)}
                        placeholder="Dupont"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prénom
                      </label>
                      <input
                        type="text"
                        value={contact.prenom}
                        onChange={(e) => updateContact(index, 'prenom', e.target.value)}
                        placeholder="Marie"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fonction
                      </label>
                      <input
                        type="text"
                        value={contact.fonction}
                        onChange={(e) => updateContact(index, 'fonction', e.target.value)}
                        placeholder="Gestionnaire"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={contact.email}
                        onChange={(e) => updateContact(index, 'email', e.target.value)}
                        placeholder="contact@example.fr"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        value={contact.telephone}
                        onChange={(e) => updateContact(index, 'telephone', e.target.value)}
                        placeholder="03 21 00 00 00"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div className="flex items-center">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={contact.principal}
                          onChange={(e) => updateContact(index, 'principal', e.target.checked)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Contact principal</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Informations complémentaires..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Boutons */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin/interlocuteurs')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Enregistrement...' : (isEditMode ? 'Mettre à jour' : 'Créer l\'interlocuteur')}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}